/* global G */
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { getFirstItem } from 'lib/util/index'

/**
 * Get the mime type of `item`.
 *
 * @param {Object} item   the attachment
 * @param {Boolean} type  return either the type (`'application/zip'` <-- zip)
 *                        or the group (`'application/zip'` <-- application)
 * @returns {String|null}
 */
export const getMimeType = (item, type = true) => (item?.type?.split?.('/') || item?.mimetype?.split?.('/') || '')[type ? 1 : 0]
    || (item?.url && item.url.split('.')?.pop()?.trim?.())
    || 'other'

/**
 * Get file type of external attachments.
 *
 * @param {Object} item    the attachment
 * @param {Object} types  object of known file types
 * @private
 */
export const getFileType = (item, types) => {
  const extension = item?.url?.split?.('.')?.at?.(-1) || 'unknown'
  return Object.keys(types).find(type => types[type].includes(extension))
}

/**
 * Attempts to get the mime group of `item`.
 *
 * @param {Object} item     the attachment
 * @param {Object} [types]  object of known file types
 * @returns {String|null}
 */
export const getMimeGroup = (item, types) => (item.type || item.mimetype
  ? getMimeType(item, false)
  : getFileType(item, types))

/**
 * Format the given `bytes` into human-readable format.
 *
 * @param {Number} bytes        the bytes to format
 * @param {Number} [decimals=2] number of decimals
 * @returns {string}
 */
export const formatFilesize = (bytes, decimals = 2) => {
  if (!+bytes) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
}

/**
 * Finds the name of the attachment group in an action
 *
 * Does so by either looking up the component defined by `componentKey` and returns its `group`
 * property or by finding the component that created the group, denoted by `groupComponentKey`
 * and returns its `name` property. If either component couldn't be found, it'll return
 * `"default"`.
 *
 * @param {Gaia.Mobile.Application|Gaia.AppModule.Spec} moduleOrSession session or module
 * @param {string|null} [componentKey=null]                             key of the component that
 *                                                                      inits the attachment upload
 *                                                                      (e.g. `btnSave`)
 * @param {string} [groupComponentKey='attachmentGroup']                key of the component that
 *                                                                      creates the group
 * @returns {string}
 */
export const findGroup = (moduleOrSession, componentKey = null, groupComponentKey = 'attachmentGroup', actionsComponentKey = 'btnConfirm') => {
  const module = moduleOrSession[G.MODULE] ? moduleOrSession[G.MODULE] : moduleOrSession
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const actions = getFirstItem(module[G.STATE][G.ACTION][G.COMPONENT][G.ACTIONS])

  const { [actionsComponentKey]: actionTarget } = actions?.[G.CHILDREN]
    ? sequenceComponentFindProxy(actions)
    : {}

  const {
    [groupComponentKey]: groupTarget,
    [componentKey]: componentTarget,
  } = sequenceComponentFindProxy(actionComponent)

  const { name: groupComponentGroup = null } = groupTarget?.[G.PROPS] || {}
  const { group: componentGroup = null } = componentTarget?.[G.PROPS] || {}

  const { group: actionGroup = null } = actionTarget?.[G.PROPS] || {}

  return componentGroup || groupComponentGroup || actionGroup || 'default'
}
